import React from "react";
import { Clock, TrendingDown, Heart } from "lucide-react";

const Benefits = () => {
  const benefits = [
    {
      number: "01.",
      title: "Libertad total",
      description: "Recupera tu tiempo y energía mientras nuestros sistemas inteligentes manejan las operaciones diarias, permitiéndote enfocarte en el crecimiento estratégico.",
      icon: <Clock className="h-6 w-6 text-orange-500" />
    },
    {
      number: "02.",
      title: "Eficiencia económica",
      description: "Optimiza tu presupuesto eliminando gastos innecesarios y maximizando el rendimiento de cada euro invertido en tecnología.",
      icon: <TrendingDown className="h-6 w-6 text-orange-500" />
    },
    {
      number: "03.",
      title: "Experiencia excepcional",
      description: "Supera las expectativas de tus clientes con respuestas instantáneas, atención personalizada y disponibilidad absoluta.",
      icon: <Heart className="h-6 w-6 text-orange-500" />
    }
  ];

  return (
    <section className="py-20 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-20">
          <h2 className="text-3xl lg:text-5xl font-bold text-gray-800 mb-4">
            Simplifica tu operación y potencia tu{" "}
            <br />
            <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              crecimiento con IA
            </span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left side - AI Network Visualization */}
          <div className="relative">
            <div className="relative w-full h-80 lg:h-96 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl overflow-hidden shadow-2xl">
              {/* Neural network pattern */}
              <div className="absolute inset-0">
                {/* Nodes */}
                <div className="absolute top-12 left-12 w-3 h-3 bg-green-400 rounded-full animate-pulse shadow-lg shadow-green-400/50"></div>
                <div className="absolute top-20 left-32 w-2 h-2 bg-red-400 rounded-full animate-pulse delay-300 shadow-lg shadow-red-400/50"></div>
                <div className="absolute top-32 left-20 w-4 h-4 bg-blue-400 rounded-full animate-pulse delay-500 shadow-lg shadow-blue-400/50"></div>
                <div className="absolute top-40 left-40 w-3 h-3 bg-purple-400 rounded-full animate-pulse delay-700 shadow-lg shadow-purple-400/50"></div>
                <div className="absolute bottom-20 left-16 w-2 h-2 bg-yellow-400 rounded-full animate-pulse delay-1000 shadow-lg shadow-yellow-400/50"></div>
                <div className="absolute bottom-32 left-36 w-3 h-3 bg-pink-400 rounded-full animate-pulse delay-200 shadow-lg shadow-pink-400/50"></div>
                
                <div className="absolute top-16 right-20 w-2 h-2 bg-green-400 rounded-full animate-pulse delay-400 shadow-lg shadow-green-400/50"></div>
                <div className="absolute top-36 right-32 w-3 h-3 bg-red-400 rounded-full animate-pulse delay-600 shadow-lg shadow-red-400/50"></div>
                <div className="absolute bottom-24 right-24 w-2 h-2 bg-blue-400 rounded-full animate-pulse delay-800 shadow-lg shadow-blue-400/50"></div>
                <div className="absolute bottom-40 right-16 w-4 h-4 bg-purple-400 rounded-full animate-pulse delay-900 shadow-lg shadow-purple-400/50"></div>

                {/* Connection lines */}
                <svg className="absolute inset-0 w-full h-full">
                  <defs>
                    <linearGradient id="connectionGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#10b981" stopOpacity="0.6" />
                      <stop offset="100%" stopColor="#ef4444" stopOpacity="0.6" />
                    </linearGradient>
                    <linearGradient id="connectionGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.6" />
                      <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.6" />
                    </linearGradient>
                  </defs>
                  <path d="M 60 60 Q 200 120 320 80" stroke="url(#connectionGradient1)" strokeWidth="2" fill="none" className="animate-pulse" />
                  <path d="M 80 140 Q 180 200 280 160" stroke="url(#connectionGradient2)" strokeWidth="1.5" fill="none" className="animate-pulse delay-500" />
                  <path d="M 160 100 L 320 200" stroke="#10b981" strokeWidth="1" fill="none" opacity="0.4" className="animate-pulse delay-700" />
                  <path d="M 60 200 Q 160 140 300 300" stroke="#ef4444" strokeWidth="1.5" fill="none" opacity="0.5" className="animate-pulse delay-300" />
                  <path d="M 200 60 L 100 260" stroke="#8b5cf6" strokeWidth="1" fill="none" opacity="0.4" className="animate-pulse delay-900" />
                </svg>
              </div>

              {/* Overlay glow effect */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
            </div>
          </div>

          {/* Right side - Benefits */}
          <div className="space-y-12">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-6 group">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center w-12 h-12 bg-orange-50 rounded-xl group-hover:bg-orange-100 transition-colors">
                    {benefit.icon}
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <span className="text-2xl font-bold text-orange-500">
                      {benefit.number}
                    </span>
                    <h3 className="text-2xl font-bold text-gray-800 group-hover:text-orange-600 transition-colors">
                      {benefit.title}
                    </h3>
                  </div>
                  <div className="w-12 h-1 bg-gradient-to-r from-orange-500 to-orange-300 rounded-full mb-4"></div>
                  <p className="text-gray-600 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Benefits;